import * as R from 'ramda';
import { deleteUserPreference, getUserPreferences, upsertUserPreference } from 'App/services/UserPreferenceService';
import { queryClient } from 'Shared/queryClient';

const USER_PREFERENCES_QUERY_KEY = ['userPreferences'];

// Fetches preferences and normalizes the response into a map of { name: { id, name, value } }.
const fetchPreferences = async () => {
    const response = await getUserPreferences();
    const prefs = Array.isArray(response) ? response : response?.data || [];
    return R.indexBy(R.prop('name'), prefs);
};

// Reads a single preference value straight from the query cache. Returns null when absent.
const getPreference = (key) => {
    const preferences = queryClient.getQueryData(USER_PREFERENCES_QUERY_KEY) || {};
    const pref = preferences[key];
    if (!pref) return null;
    return pref.value;
};

// Optimistically upserts a preference in the cache, persists it, and rolls back on failure.
const setPreference = async (key, value) => {
    await queryClient.cancelQueries(USER_PREFERENCES_QUERY_KEY);
    const previous = queryClient.getQueryData(USER_PREFERENCES_QUERY_KEY);
    queryClient.setQueryData(USER_PREFERENCES_QUERY_KEY, (old = {}) => ({
        ...old,
        [key]: { ...old[key], name: key, value },
    }));

    try {
        return await upsertUserPreference({ name: key, value });
    } catch (error) {
        if (previous) {
            queryClient.setQueryData(USER_PREFERENCES_QUERY_KEY, previous);
        }
        throw error;
    }
};

// Optimistically removes a preference from the cache, deletes it, and rolls back on failure.
const removePreference = async (key) => {
    const preferences = queryClient.getQueryData(USER_PREFERENCES_QUERY_KEY) || {};
    const pref = preferences[key];
    if (!pref?.id) return undefined;

    await queryClient.cancelQueries(USER_PREFERENCES_QUERY_KEY);
    const previous = queryClient.getQueryData(USER_PREFERENCES_QUERY_KEY);
    queryClient.setQueryData(USER_PREFERENCES_QUERY_KEY, (old = {}) => {
        const updated = { ...old };
        delete updated[key];
        return updated;
    });

    try {
        return await deleteUserPreference(pref.id);
    } catch (error) {
        if (previous) {
            queryClient.setQueryData(USER_PREFERENCES_QUERY_KEY, previous);
        }
        throw error;
    }
};

export { USER_PREFERENCES_QUERY_KEY, fetchPreferences, getPreference, removePreference, setPreference };
